import distance from "@turf/distance";
import lineDistance from "@turf/line-distance";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import find from "lodash.find";
import { generateTimeString } from "../../tools";

export function getNearestStopId(stops, coordinates) {
    return stops
        .reduce(
            (acc, stop) => [
                ...acc,
                {
                    id: stop.id,
                    distance: Math.abs(distance([stop.coords.lon, stop.coords.lat], coordinates)),
                },
            ],
            [],
        )
        .sort((a, b) => a.distance - b.distance)
        .shift().id;
}

function getRouteDistance(route) {
    return function(from, to) {
        const units = "kilometers";
        const ops = { units };

        const nearestPoints = {
            current: nearestPointOnLine(route, from, ops),
            next: nearestPointOnLine(route, to, ops),
        };

        const pathToNext = {
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: route.geometry.coordinates.slice(
                    nearestPoints.current.properties.index,
                    nearestPoints.next.properties.index + 1,
                ),
            },
        };

        return lineDistance(pathToNext, units).toFixed(1);
    };
}

export function getStops(route, loopDirection, itineraryId, startingPoint = []) {
    let items = Object.values(route.itineraryData.stops);

    //Change order if COUNTER_CLOCKWISE
    if (loopDirection === "COUNTER_CLOCKWISE") {
        items = [items[0], ...items.slice(1).reverse()].map(function(item, idx) {
            return { ...item, order: idx };
        });
    }

    //Change order if starting point has changed
    const hasPlace = items.find(stop => stop.type === "place_start");
    if (startingPoint.length && route?.routeData?.routeType?.toUpperCase() === "LOOP" && !hasPlace) {
        const startStop = find(items, {
            id: getNearestStopId(
                items.filter(stop => stop.type === "stop"),
                startingPoint,
            ),
        });
        const startIndex = items.indexOf(startStop);
        items = [...items.slice(startIndex), ...items.slice(0, startIndex)].map(function(item, idx) {
            return { ...item, order: idx };
        });
    }

    //Break apart stops and alerts to process separately
    const stops = items
        .filter(stop => ["place_overnight", "stop"].includes(stop.type))
        .map(stop => {
            if (stop.type === "stop") {
                return {
                    ...stop,
                    stopID: stop.id,
                    overnight: stop.stopType && stop.stopType.label === "Overnight",
                };
            }
            return stop;
        })
        .map(stop => ({ ...stop, coords: [stop.coords.lon, stop.coords.lat] }));

    const alerts = items.filter(stop => stop.type === "alert");

    return () => {
        const processedStops = stops.map(function(point, index, list) {
            const stop = loopDirection === "COUNTER_CLOCKWISE" ? list[index] : list[index + 1];
            const result =
                ("stopType" in point || point.type === "place_overnight") && stop
                    ? {
                          ...point,
                          distToNext: stop.distanceFromPrevStop / 1000,
                      }
                    : { ...point, distToNext: 0 };

            return {
                ...result,
                timeToNext: stop ? generateTimeString(stop.timeFromPrevStop / 60, 60) : 0,
            };
        });
        const mergedStopsAlerts = Object.values(processedStops).concat(alerts);
        const sortedItems = mergedStopsAlerts.sort((a, b) => (a.order > b.order ? 1 : -1));
        return sortedItems;
    };
}

export function legacyGetStops(route, loopDirection, itineraryId, startingPoint = []) {
    //get AvgSpeed for the route
    const speed = parseFloat(route.itineraryData.metadata.type.avgSpeed);

    let items = Object.values(route.itineraryData.stops);

    //Change order if COUNTER_CLOCKWISE
    if (loopDirection === "COUNTER_CLOCKWISE") {
        items = [items[0], ...items.slice(1).reverse()].map(function(item, idx) {
            return { ...item, order: idx };
        });
    }

    //Change order if starting point has changed
    if (startingPoint.length) {
        console.log(items);
        const startStop = find(items, {
            id: getNearestStopId(
                items.filter(stop => stop.type === "stop"),
                startingPoint,
            ),
        });
        const startIndex = items.indexOf(startStop);
        items = [...items.slice(startIndex, items.length), ...items.slice(0, startIndex)].map(function(item, idx) {
            return { ...item, order: idx };
        });
        console.log(items);
    }

    //Break apart stops and alerts to process separately
    const stops = items
        .filter(stop => stop.type === "stop")
        .map(stop => ({
            ...stop,
            stopID: stop.id,
            overnight: stop.stopType && stop.stopType.label === "Overnight",
        }))
        .map(stop => ({ ...stop, coords: [stop.coords.lon, stop.coords.lat] }));

    const alerts = items.filter(stop => stop.type === "alert");

    let points = stops;

    const itineraryPath = {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: points.map(({ coords }) => coords),
        },
    };

    const getDistance = getRouteDistance(itineraryPath);

    return () => {
        const processedStops = points.map(function(point, index, list) {
            const result =
                index < list.length - 1 && "stopType" in point && list[index + 1]
                    ? {
                          ...point,
                          distToNext: parseFloat(getDistance(point.coords, list[index + 1].coords)),
                      }
                    : { ...point, distToNext: 0 };

            return {
                ...result,
                timeToNext: generateTimeString(result.distToNext, speed),
            };
        });
        const mergedStopsAlerts = Object.values(processedStops).concat(alerts);
        const sortedItems = mergedStopsAlerts.sort((a, b) => (a.order > b.order ? 1 : -1));
        return sortedItems;
    };
}

export function getFormattedAddress(address) {
    const street = address.street ? address.street + "," : "";
    const city = address.city ? address.city + "," : "";
    const province = address.province ? address.province : "";
    return `${street} ${city} ${province}`;
}
